import {
    Button,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    Icon
} from "@material-ui/core";
import { ThemeProvider as MuiThemeProvider } from "@material-ui/core/styles";
import { Stripe } from "@stripe/stripe-js";
import moment from "moment";
import React, { Component } from "react";
import { Trans, withTranslation, WithTranslation } from "react-i18next";
import styled from "styled-components";

import { MetricName } from "common/interfaces";
import BillingController, { BillingControllerState } from "js/controllers/BillingController";
import getLogger, { LogGroup } from "js/core/logger";
import { handleStripeErrors } from "js/core/services/stripe";
import { BeautifulDialog, ShowDialogAsync, ShowErrorDialog } from "js/react/components/Dialogs/BaseDialog";
import FetchingClickShield from "js/react/components/FetchingClickShield";
import { dialogTheme } from "js/react/materialThemeOverrides";
import ChangePaymentMethodDialog from "js/react/views/Billing/dialogs/ChangePaymentMethodDialog";
import withStripe from "js/react/views/Billing/withStripe";

import "css/billing.scss";

const logger = getLogger(LogGroup.BILLING);

const AlertContainer = styled.div`
  color: red;
  font-size: 15px;
  text-transform: uppercase;
  display: inline-flex;
  align-items: center;
`;

const UpdatePaymentMessage = styled(DialogContent)`
  padding: 0 34px 20px !important;
`;

const UpdatePaymentActions = styled(DialogActions)`
  padding: 0 34px 20px !important;
`;

interface PaymentIssueDialogProps {
    onClose: () => void;
    stripe: Stripe;
}

interface PaymentIssueDialogState {
    fetching: boolean;
}

class PaymentIssueDialog extends Component<PaymentIssueDialogProps & BillingControllerState & WithTranslation, PaymentIssueDialogState> {
    state: PaymentIssueDialogState = {
        fetching: false
    };

    handleUpdatePaymentMethod = async () => {
        const { onClose } = this.props;

        this.setState({ fetching: true });

        const success = await ShowDialogAsync(ChangePaymentMethodDialog);
        if (success) {
            onClose();
            return;
        }

        this.setState({ fetching: false });
    }

    handleConfirmPayment = async () => {
        const { onClose, stripe, stripeData: { subscription } } = this.props;

        this.setState({ fetching: true });

        try {
            const latestInvoice = subscription.latest_invoice;
            const paymentIntent = latestInvoice.payment_intent;

            try {
                await handleStripeErrors(() => stripe.confirmPayment({
                    clientSecret: paymentIntent.client_secret,
                    confirmParams: {
                        return_url: window.location.href,
                        payment_method: paymentIntent.payment_method as string
                    },
                    redirect: "if_required"
                }));
                logger.metric(MetricName.PAYMENT_CONFIRMATION_ATTEMPT, {
                    success: true,
                    customerId: paymentIntent.customer,
                });
            } catch (err) {
                logger.metric(MetricName.PAYMENT_CONFIRMATION_ATTEMPT, {
                    success: false,
                    customerId: paymentIntent.customer,
                    errorMessage: err.message
                });
                throw err;
            }

            onClose();
        } catch (err) {
            this.setState({ fetching: false });

            logger.error(err, "PaymentIssueDialog stripe.handleCardPayment() failed");
            ShowErrorDialog({
                error: this.props.t("Could not confirm payment"),
                message: err.message
            });
        }
    }

    render() {
        const { onClose, stripeData: { subscription }, t } = this.props;
        const { fetching } = this.state;

        const latestInvoice = subscription.latest_invoice;
        const paymentIntent = latestInvoice?.payment_intent;
        const chargeFailureMessage = latestInvoice?.charge?.failure_message;
        const latestInvoiceAmount = latestInvoice?.total;

        const nextInvoiceDate = moment.unix(subscription.current_period_end)
            .format("MMM DD, YYYY");

        const daysUntilDowngrade = moment.unix(subscription.current_period_start)
            .add(30, "days")
            .diff(moment(), "days");

        const required2fa = paymentIntent?.status === "requires_action";

        return (
            <MuiThemeProvider theme={dialogTheme}>
                <BeautifulDialog className="update_payment_form" closeDialog={onClose}>
                    <FetchingClickShield visible={fetching} backgroundColor="white" />
                    <DialogTitle>
                        {required2fa && <>
                            <AlertContainer>
                                <Icon>error</Icon>
                                <span style={{ paddingLeft: 10 }}>{t("Please confirm your payment")}</span>
                            </AlertContainer>
                        </>}
                        {!required2fa && <>
                            <AlertContainer>
                                <Icon>error</Icon>
                                <span style={{ paddingLeft: 10 }}>{t("Payment Failed")}</span>
                            </AlertContainer>
                            <div style={{ paddingTop: 20 }}>{t("Don't lose your work!")}</div>
                        </>}
                    </DialogTitle>
                    <UpdatePaymentMessage>
                        {required2fa && <>
                            <DialogContentText>
                                {t("Your bank required additional verification to process your payment.")}
                            </DialogContentText>
                        </>}
                        {!required2fa && <>
                            <DialogContentText>
                                {t("There was a problem with your payment method.")}
                            </DialogContentText>
                            <DialogContentText>
                                <Trans
                                    t={t}
                                    components={{
                                        bold: <b />,
                                        link: (
                                            <a
                                                href="https://support.beautiful.ai/hc/en-us/sections/360006956931-Managing-your-Billing"
                                                target="_blank"
                                            />
                                        )
                                    }}
                                    values={{ days: daysUntilDowngrade }}
                                >
                                    You have <bold>{{days}} days</bold> to update your payment information before
                                    your account is downgraded. Learn more about managing your
                                    account <link>here</link>.
                                </Trans>
                            </DialogContentText>
                            {chargeFailureMessage && <DialogContentText>
                                <b>{t("Failure Reason:")}</b> {chargeFailureMessage}
                            </DialogContentText>}
                        </>}
                        {latestInvoiceAmount && <DialogContentText>
                            {t("You will be charged {{amount}} today. Your next bill will be due {{date}}.", {
                                amount: `$${latestInvoiceAmount / 100}`,
                                date: nextInvoiceDate
                            })}
                        </DialogContentText>}
                    </UpdatePaymentMessage>
                    <UpdatePaymentActions>
                        <Button color="secondary" onClick={onClose}>
                            {t("Dismiss")}
                        </Button>
                        {required2fa &&
                            <Button
                                color="primary"
                                disabled={fetching}
                                onClick={() => this.handleConfirmPayment()}>
                                {t("Confirm payment")}
                            </Button>
                        }
                        {!required2fa &&
                            <Button
                                color="primary"
                                disabled={fetching}
                                onClick={() => this.handleUpdatePaymentMethod()}>
                                {t("Update Payment Method")}
                            </Button>
                        }
                    </UpdatePaymentActions>
                </BeautifulDialog>
            </MuiThemeProvider>
        );
    }
}

export default withTranslation()(withStripe(BillingController.withInitializedState(PaymentIssueDialog)));
